import clsx from 'clsx'
import dayjs from 'dayjs'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import { defineComponent, reactive, Teleport, computed, useId } from 'vue'

import DatePicker from '@/components/common/date/DatePicker'
import SelectDateModal from '@/components/common/modal/SelectDateModal'
import TeleportContent from '@/components/common/teleport/TeleportContent'
import VTextField from '@/components/common/text-field/VTextField'
import VIcon from '@/components/common/VIcon'
import useMenuModal from '@/composables/common/modal/useMenuModal'
import useMenu from '@/composables/common/useMenu'
import { dateTextFormat } from '@/utils/date'
import { type MenuAttach } from '@/utils/element'
import { isNil } from '@/utils/validation'

dayjs.extend(buddhistEra)

interface Props {
  tData?: string
  class?: string
  inputClass?: string
  value?: dayjs.Dayjs | null
  min?: dayjs.Dayjs | null
  max?: dayjs.Dayjs | null
  placeholder?: string
  attach?: MenuAttach
  disabled?: boolean
  fullWidth?: boolean
  required?: boolean
  label?: string
  variant?: 'floating'
  error?: string | boolean
  description?: string | boolean
  isMobileMode?: boolean
  onChange?: (selectedDate: dayjs.Dayjs) => void
}

export default defineComponent<Props>((props) => {
  // #region Props Getters

  const tData = computed((): string => props.tData ?? 'v-select-date')
  const attach = computed((): MenuAttach => props.attach ?? 'body')

  // #endregion

  const selectDateModalId = useId()
  const {
    activatorRootEml,
    isFocus,
    menuContent,
    menuContentStyle,
    setIsFocus
  } = useMenu(reactive({
    attach,
    isCheckMenuContent: true,
    isFixedMinWidth: false,
    disabled: computed(() => !!props.disabled || !!props.isMobileMode)
  }))
  const selectDateModal = useMenuModal(Symbol(`${selectDateModalId}-select-date-modal`))

  // #region Getters

  const dateValue = computed((): string => isNil(props.value) ? '' : dateTextFormat(props.value))

  // #endregion

  return () => (
    <div
      ref={activatorRootEml}
      t-data={tData.value}
      class={['relative', props.class]}
    >
      <VTextField
        mainRef={activatorRootEml}
        type='text'
        inputClass={clsx(
          'truncate caret-transparent',
          props.inputClass
        )}
        placeholder={props.placeholder}
        error={props.error}
        description={props.description}
        disabled={props.disabled}
        required={props.required}
        value={dateValue.value}
        inputmode='none'
        autocomplete='off'
        isSelecteMode
        label={props.label}
        variant={props.variant}
        onKeydown={(e) => { e.preventDefault() }}
        onPaste={(e) => { e.preventDefault() }}
        onCut={(e) => { e.preventDefault() }}
        onClick={(e) => {
          if (!props.isMobileMode) {
            e.preventDefault()
          } else {
            selectDateModal.reveal()
          }
        }}
      >
        {{
          append () {
            return (
              <VIcon
                class={clsx(
                  'text-[24px]',
                  {
                    'text-neutral-800': !props.disabled,
                    'text-neutral-400': props.disabled
                  }
                )}
                icon='material-symbols:event-outline'
              />
            )
          }
        }}
      </VTextField>
      {isFocus.value &&
        <Teleport to={attach.value}>
          <TeleportContent
            t-data={`${tData.value}-menu-content`}
            mainRef={menuContent}
            class='z-1001 mt-1'
            style={menuContentStyle.value}
          >
            <DatePicker
              value={props.value}
              min={props.min}
              max={props.max}
              fullWidth={props.fullWidth}
              onChange={(newValue) => {
                props.onChange?.(newValue)
                setIsFocus(false)
              }}
            />
          </TeleportContent>
        </Teleport>}
      {selectDateModal.isRevealed.value &&
        <SelectDateModal
          t-data={`${tData.value}-modal`}
          modalkey={selectDateModal.key}
          title={props.label}
          value={props.value}
          min={props.min}
          max={props.max}
          onChange={(value) => {
            props.onChange?.(value)
            selectDateModal.cancel()
          }}
        />}
    </div>
  )
}, {
  name: 'SelectDate',
  props: [
    'tData',
    'inputClass',
    'value',
    'min',
    'max',
    'placeholder',
    'attach',
    'disabled',
    'fullWidth',
    'required',
    'label',
    'variant',
    'error',
    'description',
    'isMobileMode',
    'onChange'
  ]
})
